import type React from 'react';

import { Button, Spinner } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import type { TranslationKey, TranslationPluralKey } from '@change-corgi/core/i18n';
import { translationKey, translationPluralKey } from '@change-corgi/core/i18n';
import { Translate, TypedTranslate, TypedTranslatePlural } from '@change-corgi/core-react/i18n';

import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import { BasicCard } from './components/BasicCard';
import { ExpandedCard } from './components/ExpandedCard';
import { FeaturedCard } from './components/FeaturedCard';
import { usePageBody } from './hooks/usePageBody';

const I18N: Record<'guides' | 'news', { fetchedMore: TranslationPluralKey; fetchingMore: TranslationKey }> = {
	guides: {
		fetchedMore: translationPluralKey('corgi.guides.list.fetched_more'),
		fetchingMore: translationKey('corgi.guides.list.fetching_more'),
	},
	news: {
		fetchedMore: translationPluralKey('corgi.news.list.fetched_more'),
		fetchingMore: translationKey('corgi.news.list.fetching_more'),
	},
};

export const PageBody: React.FC = () => {
	const {
		data: { hasNextPage, items, nextPageLoading, guides },
		actions: { loadMore },
	} = usePageBody();
	const { containerRef, loadMoreRef, loadingRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	const i18n = I18N[guides ? 'guides' : 'news'];

	return (
		<div className="container px-0 pb-20" ref={containerRef}>
			<ul>
				{items.map((item, index) => {
					const fullWidth = index === 0 || index === 7;
					return (
						<li
							key={item.id}
							className={cn(
								'mb-[30px] inline-block align-top md:mb-[60px]',
								fullWidth ? 'w-full' : 'w-full px-4 md:mx-2 md:w-[calc(33.33%-16px)] md:px-0',
							)}
						>
							{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
								<div className="sr-only" role="alert">
									<TypedTranslatePlural
										value={i18n.fetchedMore}
										count={items.length - lastEvent.previousItemsCount}
										replacements={{ count: items.length - lastEvent.previousItemsCount }}
									/>
								</div>
							)}
							{(() => {
								if (index === 0) return <FeaturedCard item={item} ref={getListItemRef(index)} />;
								if (index === 7) return <ExpandedCard item={item} ref={getListItemRef(index)} />;
								return <BasicCard item={item} ref={getListItemRef(index)} />;
							})()}
						</li>
					);
				})}
			</ul>
			{nextPageLoading && (
				<>
					<div className="sr-only" role="alert">
						<TypedTranslate value={i18n.fetchingMore} />
					</div>
					{/* 44 is the height of the button */}
					<Spinner className="mx-auto mt-6" ref={loadingRef} />
				</>
			)}
			{hasNextPage && !nextPageLoading && (
				<div className="flex justify-center">
					<Button
						className="mt-6 w-full max-w-[600px]"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: items.length });
							loadMore();
						}}
						ref={loadMoreRef}
						data-qa="news-and-guides-load-more-button"
					>
						<Translate value="fe.components.load_more.load_more" />
					</Button>
				</div>
			)}
		</div>
	);
};
